import { useRef, useEffect } from 'react';
import type { POI } from '@poi-planner/shared';
import { SidebarPoiCard } from './SidebarPoiCard';
import clsx from 'clsx';

interface LibraryPoiListProps {
  pois: POI[];
  selectedPoiId: string | null;
  onSelectPoi: (id: string) => void;
  onEditPoi: (id: string) => void;
  /** Distance from target POI for each POI */
  distanceMap?: Map<string, number>;
  /** Whether to show distance badges */
  showDistance?: boolean;
  /** Currently targeted POI ID */
  targetPoiId?: string | null;
  /** Callback when user sets a POI as target */
  onSetTarget?: (poiId: string | null) => void;
  /** Selection mode for bulk operations */
  selectionMode?: boolean;
  /** Currently selected POI IDs in selection mode */
  selectedIds?: Set<string>;
  /** Toggle selection of a POI */
  onToggleSelection?: (poiId: string) => void;
}

export function LibraryPoiList({
  pois,
  selectedPoiId,
  onSelectPoi,
  onEditPoi,
  distanceMap,
  showDistance = false,
  targetPoiId,
  onSetTarget,
  selectionMode = false,
  selectedIds,
  onToggleSelection,
}: LibraryPoiListProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  // Scroll to selected POI when it changes (e.g., from map click)
  useEffect(() => {
    if (selectedPoiId && containerRef.current) {
      const cardEl = containerRef.current.querySelector(`[data-poi-id="${selectedPoiId}"]`);
      if (cardEl) {
        cardEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
  }, [selectedPoiId]);

  if (pois.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center py-12 px-6">
        <span className="material-symbols-outlined text-5xl text-gray-300 mb-4">
          explore
        </span>
        <p className="text-sm font-semibold text-text-secondary mb-1">
          No POIs found
        </p>
        <p className="text-xs text-text-muted text-center leading-relaxed">
          Adjust your filters or create a new POI
        </p>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="flex-1 overflow-y-auto p-3 flex flex-col gap-2 custom-scrollbar">
      {pois.map((poi) => {
        const isChecked = selectedIds?.has(poi.id) ?? false;

        if (selectionMode) {
          return (
            <button
              key={poi.id}
              type="button"
              onClick={() => onToggleSelection?.(poi.id)}
              className={clsx(
                'flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left w-full',
                isChecked
                  ? 'border-amber-400 bg-amber-50'
                  : 'border-border bg-white hover:border-slate-300'
              )}
            >
              {/* Checkbox */}
              <div
                className={clsx(
                  'flex items-center justify-center size-6 rounded-md border-2 transition-all shrink-0',
                  isChecked
                    ? 'bg-amber-500 border-amber-500 text-white'
                    : 'bg-white border-slate-300'
                )}
              >
                {isChecked && (
                  <span className="material-symbols-outlined text-base">check</span>
                )}
              </div>

              {/* POI info */}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-text-primary truncate">
                  {poi.name}
                </div>
                {poi.tags && poi.tags.length > 0 && (
                  <div className="text-xs text-text-muted mt-0.5 truncate">
                    {poi.tags.slice(0, 3).join(', ')}
                    {poi.tags.length > 3 && ` +${poi.tags.length - 3}`}
                  </div>
                )}
              </div>

              {/* Distance badge in selection mode */}
              {showDistance && distanceMap?.get(poi.id) !== undefined && (
                <div className="text-xs text-text-muted shrink-0">
                  {distanceMap.get(poi.id)!.toFixed(1)} km
                </div>
              )}
            </button>
          );
        }

        return (
          <SidebarPoiCard
            key={poi.id}
            poi={poi}
            isSelected={poi.id === selectedPoiId}
            isTargeted={poi.id === targetPoiId}
            distance={distanceMap?.get(poi.id)}
            showDistance={showDistance}
            onClick={() => onSelectPoi(poi.id)}
            onEdit={() => onEditPoi(poi.id)}
            onSetTarget={onSetTarget}
          />
        );
      })}
    </div>
  );
}
